import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/**
 * BannerCarousel — cinematic hero carousel on the public landing page.
 *
 * Fetches active banners from `/api/banners` (CMS-driven by AdminBanners).
 * - Auto-rotates every 4 seconds with a smooth horizontal slide.
 * - Pauses rotation on hover/touch.
 * - Respects reduced-motion preferences.
 * - Clickable dots indicator + keyboard arrow navigation.
 * - Renders absolutely nothing when no banners exist, so the rest of the
 *   landing page keeps its original cinematic flow.
 */
const ROTATE_MS = 4000;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BannerCarousel() {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/banners`)
      .then((r) => { if (alive) setBanners(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setBanners([]); });
    return () => { alive = false; };
  }, []);

  const next = useCallback(() => {
    setIdx((i) => (banners.length ? (i + 1) % banners.length : 0));
  }, [banners.length]);

  const prev = useCallback(() => {
    setIdx((i) => (banners.length ? (i - 1 + banners.length) % banners.length : 0));
  }, [banners.length]);

  // Auto-rotation timer
  useEffect(() => {
    if (banners.length < 2 || paused) return undefined;
    timerRef.current = setInterval(next, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [banners.length, paused, next]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // When there are no banners, we still reserve the same vertical offset that
  // a rendered banner would claim — so the Hero below never jams under the
  // fixed Nav + sticky BatchesRibbon. This keeps the rhythm identical with or
  // without banners (founder spec — a deleted banner must NOT break the flow).
  if (!banners.length) {
    return (
      <div
        aria-hidden
        className="w-full pt-[96px] sm:pt-[108px] md:pt-[120px]"
        data-testid="landing-banner-spacer"
      />
    );
  }

  const active = banners[idx] || banners[0];
  const isExternal = (href) => /^https?:\/\//i.test(href || "");

  return (
    <section
      className="relative z-[5] w-full px-3 sm:px-5 md:px-10 pt-[96px] sm:pt-[108px] md:pt-[120px] pb-2 sm:pb-4"
      data-testid="landing-banner-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
    >
      <div className="max-w-[1600px] mx-auto relative">
        <div
          className="banner-aspect relative w-full rounded-2xl md:rounded-[28px] overflow-hidden border border-white/10 shadow-[0_20px_60px_-20px_rgba(59,130,246,0.30)]"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active.id || idx}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0"
              style={{
                background: `linear-gradient(120deg, ${active.bg_from || "#0b1e3a"}, ${active.bg_to || "#153f75"})`,
              }}
            >
              {/* Image layer — `object-cover` on ALL viewports so banner stretches
                  edge-to-edge with no empty letterbox space. Mobile aspect ratio
                  matches the admin 1600×540 upload guide so almost nothing crops
                  there; desktop flattens to 4:1 / 21:5 for cinematic feel. */}
              {active.image_url && (
                <img
                  src={active.image_url}
                  alt={active.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              {/* Readability scrim — stronger on the left so text stays legible */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(0,8,22,0.78) 0%, rgba(0,8,22,0.45) 40%, rgba(0,8,22,0.15) 75%, rgba(0,8,22,0) 100%)",
                }}
              />
              {/* Text block */}
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-[62%] sm:max-w-[56%] md:max-w-[52%] px-5 sm:px-8 md:px-12">
                  <h2
                    className="lms-display text-white leading-[1.05] text-xl sm:text-2xl md:text-4xl lg:text-5xl line-clamp-3"
                    style={{ textShadow: "0 2px 16px rgba(0,0,0,0.35)" }}
                  >
                    {active.title}
                  </h2>
                  {active.subtitle && (
                    <p className="text-white/80 text-[11px] sm:text-xs md:text-base mt-2 sm:mt-3 leading-relaxed line-clamp-2 md:line-clamp-3">
                      {active.subtitle}
                    </p>
                  )}
                  {active.cta_text && active.cta_link && (
                    isExternal(active.cta_link) ? (
                      <a
                        href={active.cta_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lw-metallic lw-metallic--btn inline-flex items-center gap-1.5 mt-3 sm:mt-4 md:mt-5 !py-2 sm:!py-2.5 !px-4 sm:!px-5 text-[11px] sm:text-xs md:text-sm font-semibold hover:scale-[1.02] transition"
                        data-testid="banner-cta"
                      >
                        {active.cta_text} <ArrowUpRight size={14} />
                      </a>
                    ) : (
                      <a
                        href={active.cta_link}
                        className="lw-metallic lw-metallic--btn inline-flex items-center gap-1.5 mt-3 sm:mt-4 md:mt-5 !py-2 sm:!py-2.5 !px-4 sm:!px-5 text-[11px] sm:text-xs md:text-sm font-semibold hover:scale-[1.02] transition"
                        data-testid="banner-cta"
                      >
                        {active.cta_text} <ArrowUpRight size={14} />
                      </a>
                    )
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Progress bar — resets every slide when not paused */}
          {banners.length > 1 && !paused && (
            <motion.div
              key={`progress-${idx}`}
              className="absolute left-0 bottom-0 h-[3px]"
              style={{ background: active.accent_color || "#3b82f6" }}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
            />
          )}
        </div>

        {/* Dots indicator */}
        {banners.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-3">
            {banners.map((b, i) => (
              <button
                key={b.id || i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === idx ? "w-10 bg-[#5ceeff]" : "w-2.5 bg-white/25 hover:bg-white/40"
                }`}
                data-testid={`banner-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
